<template>
  <div>
    <Breadcrumbs main="Турниры" title="Список турниров"/>

    <Modal
        name="DeleteTournament"
        title="Удаление турнира"
        v-model="isShowDeleteTournamentModal"
        :params="{ id: selectedTournamentId }"
    />

    <!-- Container-fluid starts-->
    <div class="container-fluid tournaments-page">

      <!-- ======== Фильтры вынесены в отдельную панель ======== -->
      <TournamentsFilters
          ref="filters"
          :filters="{ search: search, game: gameFilter, variation: variationFilter }"
          :games="games"
          :variations="variations"
          :total="total"
          :loading="isLoadingTournaments"
          @change="onFilterChange"
          @reset="resetFilters"
      >
        <template #actions>
          <router-link :to="{ name: 'create_tournament' }" class="btn btn-success">
            <i class="fa fa-plus"></i> Создать турнир
          </router-link>
        </template>
      </TournamentsFilters>

      <div class="card">

        <!-- ======== Таблица ======== -->
        <div class="card-body tl-body">
          <b-overlay :show="isLoadingTournaments" rounded spinner-variant="primary" opacity="0.6">
            <b-table
                responsive
                ref="table"
                show-empty
                hover
                :items="tournaments"
                :fields="tablefields"
                :tbody-tr-class="rowClass"
                class="tl-table mb-0"
            >
              <!-- ID -->
              <template v-slot:cell(id)="{ item }">
                <button
                    type="button"
                    class="tl-id"
                    v-b-tooltip.hover.top="'Скопировать ID'"
                    @click="copyId(item.id)"
                >{{ item.id }}</button>
              </template>

              <!-- Турнир: название + дисциплина/организатор -->
              <template v-slot:cell(name)="{ item }">
                <router-link
                    :to="{ name: 'tournament_edit', params: { id: item.id } }"
                    target="_blank"
                    rel="noopener"
                    class="tl-name"
                >{{ item.name }}</router-link>
                <div class="tl-meta">
                  <span v-if="item.game && item.game.name">{{ item.game.name }}</span>
                  <span v-if="item.organizer && item.organizer.name">{{ item.organizer.name }}</span>
                  <span v-if="item.variation && item.variation.label">{{ item.variation.label }}</span>
                </div>
              </template>

              <!-- Заявки: заполнение + разбивка по статусам -->
              <template v-slot:cell(fill)="{ item }">
                <div class="tl-fill">
                  <div class="tl-fill__head">
                    <span class="tl-fill__count">
                      {{ item.accepted_entries_count || 0 }}<span class="text-muted">/{{ item.slot_count || 0 }}</span>
                    </span>
                    <span class="tl-fill__pct" :class="'text-' + fillVariant(item)">
                      {{ fillPercent(item) }}%
                    </span>
                  </div>
                  <b-progress
                      :max="item.slot_count || 1"
                      height="5px"
                      class="tl-fill__bar"
                  >
                    <b-progress-bar
                        :value="item.accepted_entries_count || 0"
                        :variant="fillVariant(item)"
                    />
                  </b-progress>
                  <div class="tl-fill__legend">
                    <span class="text-success" v-b-tooltip.hover.bottom="'Принятые заявки'">
                      <i class="fa fa-check"></i> {{ item.accepted_entries_count || 0 }}
                    </span>
                    <router-link
                        v-if="item.awaited_entries_count > 0"
                        :to="{ name: 'tournament_teams', params: { id: item.id } }"
                        target="_blank"
                        rel="noopener"
                        class="tl-fill__pending"
                        v-b-tooltip.hover.bottom="'Ожидают модерации — открыть заявки'"
                    >
                      <i class="fa fa-clock-o"></i> {{ item.awaited_entries_count }}
                    </router-link>
                    <span v-else class="text-muted" v-b-tooltip.hover.bottom="'Ожидают модерации'">
                      <i class="fa fa-clock-o"></i> 0
                    </span>
                    <span class="text-danger" v-b-tooltip.hover.bottom="'Отклонённые заявки'">
                      <i class="fa fa-times"></i> {{ item.rejected_entries_count || 0 }}
                    </span>
                  </div>
                </div>
              </template>

              <!-- Статус -->
              <template v-slot:cell(status)="{ item }">
                <b-badge
                    :variant="statusMeta(item.status).variant"
                    class="tl-status"
                    v-b-tooltip.hover.top="(item.status && item.status.code) || ''"
                >{{ statusMeta(item.status).label }}</b-badge>
              </template>

              <!-- Старт турнира -->
              <template v-slot:cell(start_time)="{ item }">
                <div class="tl-date">{{ formatDate(item.start_time) }}</div>
                <div class="tl-date__rel" :class="{ 'tl-date__rel--soon': isStartingSoon(item) }">
                  {{ humanizeRelative(item.start_time) }}
                </div>
              </template>

              <!-- Действия -->
              <template v-slot:cell(action)="{ item }">
                <TournamentRowActions
                    :tournament="item"
                    @rating="calculateTeamsRatingForTournament"
                    @recalculate="recalculateTeamsRatingForTournament"
                    @delete="setTournamentId"
                />
              </template>

              <!-- Пустые состояния -->
              <template #empty>
                <div class="tl-empty">
                  <i class="fa fa-trophy"></i>
                  <p v-if="hasActiveFilters">
                    По заданным условиям ничего не найдено.
                  </p>
                  <p v-else>Турниров пока нет.</p>
                  <b-button
                      v-if="hasActiveFilters"
                      variant="outline-primary"
                      size="sm"
                      @click="resetFilters"
                  >Сбросить фильтры
                  </b-button>
                </div>
              </template>
            </b-table>
          </b-overlay>

          <!-- ======== Пагинация ======== -->
          <div class="tl-footer">
            <div class="tl-footer__info">
              <template v-if="total">Показано {{ fromRow }}–{{ toRow }} из {{ total }}</template>
            </div>
            <b-pagination
                :value="page"
                :total-rows="total"
                :per-page="perPage"
                first-number
                last-number
                size="sm"
                class="my-0"
                @change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Container-fluid Ends-->
  </div>
</template>

<script>
import {TournamentAdapterMixin} from "../../mixins/Tournament/TournamentAdapterMixin";
import {ModalAdapterMixin} from "@/mixins/modal/ModalAdapterMixin";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import Modal from "@/components/modals/index.vue";
import TournamentsFilters from "@/components/Tournament/TournamentsFilters.vue";
import TournamentRowActions from "@/components/Tournament/TournamentRowActions.vue";
import api from "@/api/api";
import moment from "moment";
import Vue from "vue";

const DATE_FORMATS = ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601];

const STATUS_MAP = {
  1: {label: "Рег. не начата", variant: "secondary"},
  2: {label: "Регистрация", variant: "info"},
  3: {label: "Идёт", variant: "success"},
  4: {label: "Завершён", variant: "dark"},
};

export default {
  name: "TournamentsList",
  components: {Modal, TournamentsFilters, TournamentRowActions},
  mixins: [TournamentAdapterMixin, ModalAdapterMixin, PaginationHelperMixin],
  data() {
    return {
      pageKey: "pageNumberTournamentsList",
      perPage: 10,
      tablefields: [
        {key: "id", label: "ID", sortable: true, thClass: "tl-th-id"},
        {key: "name", label: "Турнир", sortable: true},
        {key: "format.name", label: "Формат", sortable: true, thClass: "tl-th-format"},
        {
          key: "fill",
          label: "Заявки / слоты",
          sortable: true,
          thClass: "tl-th-fill",
          sortByFormatted: (value, key, item) => {
            const slots = Number(item && item.slot_count) || 0;
            const accepted = Number(item && item.accepted_entries_count) || 0;
            return slots ? accepted / slots : -1;
          },
        },
        {
          key: "status",
          label: "Статус",
          sortable: true,
          thClass: "tl-th-status",
          sortByFormatted: (value, key, item) => (item && item.status && item.status.id) || 0,
        },
        {key: "start_time", label: "Старт турнира", sortable: true, thClass: "tl-th-date"},
        {key: "action", label: "Действия", sortable: false, thClass: "tl-th-actions"},
      ],
      isShowDeleteTournamentModal: false,
      selectedTournamentId: null,
      gameIds: [],
    };
  },
  computed: {
    // ---- состояние фильтров живёт в url: ссылку можно переслать, f5 и «назад» работают
    page() {
      return Number(this.$route.query.page) || 1;
    },
    search() {
      return this.$route.query.search || "";
    },
    gameFilter: {
      get() {
        return this.$route.query.game ? Number(this.$route.query.game) : null;
      },
      set(value) {
        this.updateQuery({game: value, page: 1});
      },
    },
    variationFilter: {
      get() {
        return this.$route.query.variation_id ? Number(this.$route.query.variation_id) : null;
      },
      set(value) {
        this.updateQuery({variation_id: value, page: 1});
      },
    },
    hasActiveFilters() {
      return Boolean(this.search || this.gameFilter !== null || this.variationFilter !== null);
    },

    fromRow() {
      return (this.page - 1) * this.perPage + 1;
    },
    toRow() {
      return Math.min(this.page * this.perPage, this.total || 0);
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onGlobalKeydown);

    localStorage.removeItem("selectedStageId");
    localStorage.removeItem("selectedGroupId");

    this.loadReferences();

    // возвращаемся на последнюю открытую страницу, если пришли без параметров
    const savedPage = Number(this.getCurrentPageNumber(this.pageKey)) || 1;
    if (!Object.keys(this.$route.query).length && savedPage > 1) {
      this.updateQuery({page: savedPage});
      return;
    }

    this.fetchTournaments();
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onGlobalKeydown);
  },
  watch: {
    "$route.query": {
      handler(newQuery, oldQuery) {
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;
        this.fetchTournaments();
      },
    },
    // после закрытия модалки удаления обновляем список с учётом фильтров
    isShowDeleteTournamentModal(isShown, wasShown) {
      if (wasShown && !isShown) this.onAfterDelete();
    },
    games() {
      this.gameIds = (this.games || []).map((game) => game.value);
    },
  },
  methods: {
    // ================= данные =================
    async fetchTournaments() {
      this.isLoadingTournaments = true;
      try {
        this.selectedGameId = this.gameFilter;
        this.recordPageToStorage(this.page, this.pageKey);

        await this.indexTournaments({
          pageNumber: this.page,
          ...(this.search && {search: this.search}),
          ...(this.gameFilter !== null && {gameId: this.gameFilter}),
          ...(this.variationFilter !== null && {variation_id: this.variationFilter}),
        });
      } catch (error) {
        Vue.toasted.error("Не удалось загрузить список турниров", {
          position: "top-right",
          duration: 3000,
        });
      } finally {
        this.isLoadingTournaments = false;
      }
    },

    // справочники нужны страницам создания/редактирования, грузим фоном
    loadReferences() {
      Promise.all([
        this.indexFormat({}),
        this.indexTimeZones(),
        this.getCountriesQuery(),
        this.grabGames(),
        this.grabVariations(),
      ])
          .then(() => {
            this.gameIds = (this.games || []).map((game) => game.value);
          })
          .catch(() => {
          });
    },

    async onAfterDelete() {
      // если удалили последний турнир на странице — возвращаемся на предыдущую
      if (this.page > 1 && (this.tournaments || []).length === 0) {
        this.updateQuery({page: this.page - 1});
        return;
      }
      await this.fetchTournaments();
    },

    // ================= фильтры =================
    updateQuery(patch, replace = false) {
      const query = {...this.$route.query};

      Object.keys(patch).forEach((key) => {
        const value = patch[key];
        if (value === null || value === undefined || value === "" || (key === "page" && value === 1)) {
          delete query[key];
        } else {
          query[key] = String(value);
        }
      });

      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return;

      const navigate = replace ? this.$router.replace : this.$router.push;
      navigate.call(this.$router, {path: this.$route.path, query}).catch(() => {
      });
    },

    // патч приходит из панели фильтров: {search} | {game} | {variation}
    onFilterChange(patch) {
      const query = {page: 1};

      if ("search" in patch) query.search = patch.search;
      if ("game" in patch) query.game = patch.game;
      if ("variation" in patch) query.variation_id = patch.variation;

      // правку поиска не пишем в историю браузера, чтобы «назад» не листал буквы
      this.updateQuery(query, "search" in patch);
    },

    resetFilters() {
      this.updateQuery({search: null, game: null, variation_id: null, page: 1});
    },

    onPageChange(pageNumber) {
      this.updateQuery({page: pageNumber});
      window.scrollTo({top: 0, behavior: "smooth"});
    },

    onGlobalKeydown(event) {
      if (event.key !== "/" || event.ctrlKey || event.metaKey || event.altKey) return;
      const tag = (event.target && event.target.tagName) || "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || event.target.isContentEditable) return;

      event.preventDefault();
      if (this.$refs.filters) this.$refs.filters.focusSearch();
    },

    // ================= отображение =================
    fillPercent(item) {
      const slots = Number(item && item.slot_count) || 0;
      if (!slots) return 0;
      const accepted = Number(item && item.accepted_entries_count) || 0;
      return Math.round((accepted / slots) * 100);
    },

    fillVariant(item) {
      const percent = this.fillPercent(item);
      if (percent >= 100) return "success";
      if (percent >= 50) return "primary";
      if (percent > 0) return "warning";
      return "danger";
    },

    statusMeta(status) {
      const known = status && STATUS_MAP[status.id];
      if (known) return known;
      return {label: (status && status.code) || "—", variant: "light"};
    },

    formatDate(value) {
      if (!value) return "—";
      const date = moment(value, DATE_FORMATS);
      return date.isValid() ? date.format("DD.MM.YYYY HH:mm") : "—";
    },

    humanizeRelative(value) {
      const date = moment(value, DATE_FORMATS);
      if (!date.isValid()) return "";

      const diffMinutes = date.diff(moment(), "minutes");
      const absolute = Math.abs(diffMinutes);

      let text;
      if (absolute < 1) text = "меньше минуты";
      else if (absolute < 60) text = `${absolute} мин`;
      else if (absolute < 60 * 24) text = `${Math.round(absolute / 60)} ч`;
      else text = `${Math.round(absolute / (60 * 24))} дн`;

      return diffMinutes >= 0 ? `через ${text}` : `${text} назад`;
    },

    isStartingSoon(item) {
      const statusId = item && item.status && item.status.id;
      if (![1, 2].includes(statusId)) return false;

      const date = moment(item.start_time, DATE_FORMATS);
      if (!date.isValid()) return false;

      const diffHours = date.diff(moment(), "hours", true);
      return diffHours >= 0 && diffHours <= 24;
    },

    rowClass(item) {
      if (!item) return null;
      return this.isStartingSoon(item) ? "tl-row--soon" : null;
    },

    // ================= действия =================
    async copyId(id) {
      try {
        await navigator.clipboard.writeText(String(id));
        Vue.toasted.success(`ID ${id} скопирован`, {position: "top-right", duration: 1500});
      } catch (error) {
        // буфер обмена недоступен — молча игнорируем
      }
    },

    setTournamentId(id) {
      this.selectedTournamentId = id;
      this.isShowDeleteTournamentModal = true;
    },

    async recalculateTeamsRatingForTournament(tournamentId) {
      try {
        await api.tournament.recalculateRating(tournamentId);
        Vue.toasted.success("Перерасчет запущен", {position: "top-right", duration: 3000});
      } catch (error) {
        Vue.toasted.error(
            (error.response && error.response.data && error.response.data.message) ||
            "Перерасчет запустить не удалось",
            {position: "top-right", duration: 3000}
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.tournaments-page {
  .card {
    overflow: visible;
  }
}

/* ---------- таблица ---------- */
.tl-body {
  padding-top: 20px;
}

.tl-table ::v-deep {
  th {
    white-space: nowrap;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #7e7e7e;
    vertical-align: middle;
  }

  td {
    vertical-align: middle;
  }

  .tl-th-id {
    width: 80px;
  }

  .tl-th-format {
    width: 110px;
  }

  .tl-th-fill {
    width: 170px;
  }

  .tl-th-status {
    width: 130px;
  }

  .tl-th-date {
    width: 150px;
  }

  .tl-th-actions {
    width: 1%;
  }

  .tl-row--soon > td:first-child {
    box-shadow: inset 3px 0 0 0 #ffaa05;
  }
}

.tl-id {
  padding: 0;
  border: 0;
  background: none;
  font-family: monospace;
  font-size: 13px;
  color: #7366ff;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.tl-name {
  display: block;
  font-weight: 600;
  color: #2c323f;
  line-height: 1.3;

  &:hover {
    color: #7366ff;
  }
}

.tl-meta {
  margin-top: 2px;
  font-size: 12px;
  color: #898989;

  span + span::before {
    content: "·";
    margin: 0 5px;
  }
}

/* ---------- заявки ---------- */
.tl-fill {
  min-width: 150px;

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 13px;
  }

  &__count {
    font-weight: 600;
  }

  &__pct {
    font-size: 12px;
  }

  &__bar {
    margin: 4px 0 5px;
  }

  &__legend {
    display: flex;
    gap: 10px;
    font-size: 12px;

    i {
      font-size: 10px;
    }
  }

  &__pending {
    color: #ffaa05;
    font-weight: 600;

    &:hover {
      color: #d98d00;
      text-decoration: underline;
    }
  }
}

.tl-status {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 9px;
}

/* ---------- дата ---------- */
.tl-date {
  font-size: 13px;
  white-space: nowrap;

  &__rel {
    font-size: 12px;
    color: #898989;
    white-space: nowrap;

    &--soon {
      color: #ffaa05;
      font-weight: 600;
    }
  }
}

/* ---------- пустое состояние ---------- */
.tl-empty {
  padding: 40px 15px;
  text-align: center;
  color: #898989;

  i {
    font-size: 32px;
    opacity: 0.35;
  }

  p {
    margin: 12px 0;
  }
}

/* ---------- футер ---------- */
.tl-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1f1f1;

  &__info {
    font-size: 13px;
    color: #898989;
  }
}

</style>
